import {
    createColumnHelper,
} from "@tanstack/react-table";

const columnHelper =
    createColumnHelper();


const formatDate = (
    value
) => {

    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleDateString(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    );
};


const formatCurrency = (
    value
) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "-";
    }

    const amount =
        Number(value);

    if (
        Number.isNaN(amount)
    ) {
        return `₹${value}`;
    }

    return `₹${amount.toLocaleString(
        "en-IN",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    )}`;
};


const Status = ({
    value,
}) => {

    const status =
        String(
            value || "-"
        ).toLowerCase();


    const classes = {

        draft:
            "border-gray-200 bg-gray-50 text-gray-700",

        planned:
            "border-yellow-200 bg-yellow-50 text-yellow-700",

        started:
            "border-indigo-200 bg-indigo-50 text-indigo-700",

        submitted:
            "border-blue-200 bg-blue-50 text-blue-700",

        approved:
            "border-green-200 bg-green-50 text-green-700",

        verified:
            "border-green-200 bg-green-50 text-green-700",

        rejected:
            "border-red-200 bg-red-50 text-red-700",

        paid:
            "border-emerald-200 bg-emerald-50 text-emerald-700",

        cancelled:
            "border-orange-200 bg-orange-50 text-orange-700",

    };


    return (
        <span
            className={`
                inline-flex
                rounded-full
                border
                px-3
                py-1
                text-xs
                font-semibold
                capitalize
                ${classes[status] ||
                    "border-gray-200 bg-gray-50 text-gray-700"}
            `}
        >
            {
                status.replace(
                    /_/g,
                    " "
                )
            }
        </span>
    );
};


/*
|--------------------------------------------------------------------------
| GENERIC REPORT COLUMNS
|--------------------------------------------------------------------------
*/

export const reportColumns = (
    reportType
) => {

    switch (
        reportType
    ) {

        /*
        |--------------------------------------------------------------------------
        | DAILY WORKING
        |--------------------------------------------------------------------------
        */

        case "daily_working":
        case "attendance":

            return [

                columnHelper.accessor(
                    "date",
                    {
                        id: "date",
                        header: "Date",
                        cell: (info) =>
                            formatDate(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "employee.name",
                    {
                        id: "employee",
                        header: "Employee",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "employee.employeeCode",
                    {
                        id: "employeeCode",
                        header: "Code",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "checkIn",
                    {
                        id: "checkIn",
                        header: "Check In",
                        cell: (info) =>
                            info.getValue()
                                ? new Date(
                                    info.getValue()
                                ).toLocaleTimeString(
                                    "en-IN",
                                    {
                                        hour:
                                            "2-digit",
                                        minute:
                                            "2-digit",
                                    }
                                )
                                : "-",
                    }
                ),

                columnHelper.accessor(
                    "checkOut",
                    {
                        id: "checkOut",
                        header: "Check Out",
                        cell: (info) =>
                            info.getValue()
                                ? new Date(
                                    info.getValue()
                                ).toLocaleTimeString(
                                    "en-IN",
                                    {
                                        hour:
                                            "2-digit",
                                        minute:
                                            "2-digit",
                                    }
                                )
                                : "-",
                    }
                ),

                columnHelper.accessor(
                    "status",
                    {
                        id: "status",
                        header: "Status",
                        cell: (info) => (
                            <Status
                                value={
                                    info.getValue()
                                }
                            />
                        ),
                    }
                ),

            ];


        /*
        |--------------------------------------------------------------------------
        | VISITS
        |--------------------------------------------------------------------------
        */

        case "visits":

            return [

                columnHelper.accessor(
                    "visitDate",
                    {
                        id: "visitDate",
                        header: "Date",
                        cell: (info) =>
                            formatDate(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "employee.name",
                    {
                        id: "employee",
                        header: "Employee",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "visitType",
                    {
                        id: "visitType",
                        header: "Type",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.display({
                    id: "customer",
                    header: "Customer",
                    cell: ({ row }) => {

                        const item =
                            row.original;

                        return (
                            item.school?.name ||
                            item.party?.name ||
                            "-"
                        );
                    },
                }),

                columnHelper.accessor(
                    "purpose",
                    {
                        id: "purpose",
                        header: "Purpose",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "status",
                    {
                        id: "status",
                        header: "Status",
                        cell: (info) => (
                            <Status
                                value={
                                    info.getValue()
                                }
                            />
                        ),
                    }
                ),

            ];


        /*
        |--------------------------------------------------------------------------
        | EXPENSES
        |--------------------------------------------------------------------------
        */

        case "expenses":

            return [

                columnHelper.accessor(
                    "expenseDate",
                    {
                        id: "expenseDate",
                        header: "Date",
                        cell: (info) =>
                            formatDate(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "employee.name",
                    {
                        id: "employee",
                        header: "Employee",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "expenseType",
                    {
                        id: "expenseType",
                        header: "Type",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "title",
                    {
                        id: "title",
                        header: "Title",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "claimedAmount",
                    {
                        id: "claimedAmount",
                        header: "Claimed",
                        cell: (info) =>
                            formatCurrency(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "approvedAmount",
                    {
                        id: "approvedAmount",
                        header: "Approved",
                        cell: (info) =>
                            formatCurrency(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "status",
                    {
                        id: "status",
                        header: "Status",
                        cell: (info) => (
                            <Status
                                value={
                                    info.getValue()
                                }
                            />
                        ),
                    }
                ),

            ];


        /*
        |--------------------------------------------------------------------------
        | TA / DA
        |--------------------------------------------------------------------------
        */

        case "ta_da":

            return [

                columnHelper.accessor(
                    "claimDate",
                    {
                        id: "claimDate",
                        header: "Date",
                        cell: (info) =>
                            formatDate(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "employee.name",
                    {
                        id: "employee",
                        header: "Employee",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "title",
                    {
                        id: "title",
                        header: "Title",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "claimedAmount",
                    {
                        id: "claimedAmount",
                        header: "Claimed",
                        cell: (info) =>
                            formatCurrency(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "approvedAmount",
                    {
                        id: "approvedAmount",
                        header: "Approved",
                        cell: (info) =>
                            formatCurrency(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "status",
                    {
                        id: "status",
                        header: "Status",
                        cell: (info) => (
                            <Status
                                value={
                                    info.getValue()
                                }
                            />
                        ),
                    }
                ),

            ];


        /*
        |--------------------------------------------------------------------------
        | COLLECTIONS / PAYMENTS
        |--------------------------------------------------------------------------
        */

        case "collections":
        case "payments":

            return [

                columnHelper.accessor(
                    "collectionDate",
                    {
                        id: "collectionDate",
                        header: "Date",
                        cell: (info) =>
                            formatDate(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "employee.name",
                    {
                        id: "employee",
                        header: "Employee",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.display({
                    id: "customer",
                    header: "Customer",
                    cell: ({ row }) => {

                        const item =
                            row.original;

                        return (
                            item.party?.name ||
                            item.school?.name ||
                            "-"
                        );
                    },
                }),

                columnHelper.accessor(
                    "balanceType",
                    {
                        id: "balanceType",
                        header: "Type",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "amount",
                    {
                        id: "amount",
                        header: "Amount",
                        cell: (info) =>
                            formatCurrency(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "mode",
                    {
                        id: "mode",
                        header: "Mode",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "status",
                    {
                        id: "status",
                        header: "Status",
                        cell: (info) => (
                            <Status
                                value={
                                    info.getValue()
                                }
                            />
                        ),
                    }
                ),

            ];


        /*
        |--------------------------------------------------------------------------
        | APPRAISALS
        |--------------------------------------------------------------------------
        */

        case "appraisals":

            return [

                columnHelper.accessor(
                    "appraisalDate",
                    {
                        id: "appraisalDate",
                        header: "Date",
                        cell: (info) =>
                            formatDate(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "employee.name",
                    {
                        id: "employee",
                        header: "Employee",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "school.name",
                    {
                        id: "school",
                        header: "School",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "competitor",
                    {
                        id: "competitor",
                        header: "Competitor",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "expectedOrder",
                    {
                        id: "expectedOrder",
                        header: "Expected Order",
                        cell: (info) =>
                            formatCurrency(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "booksCount",
                    {
                        id: "booksCount",
                        header: "Books",
                        cell: (info) =>
                            info.getValue() ||
                            0,
                    }
                ),

                columnHelper.accessor(
                    "status",
                    {
                        id: "status",
                        header: "Status",
                        cell: (info) => (
                            <Status
                                value={
                                    info.getValue()
                                }
                            />
                        ),
                    }
                ),

            ];


        /*
        |--------------------------------------------------------------------------
        | SAMPLES
        |--------------------------------------------------------------------------
        */

        case "samples":

            return [

                columnHelper.accessor(
                    "issueDate",
                    {
                        id: "issueDate",
                        header: "Issue Date",
                        cell: (info) =>
                            formatDate(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "employee.name",
                    {
                        id: "employee",
                        header: "Sales Person",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "school.name",
                    {
                        id: "school",
                        header: "School",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "book.title",
                    {
                        id: "book",
                        header: "Book",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "quantity",
                    {
                        id: "quantity",
                        header: "Qty",
                    }
                ),

                columnHelper.accessor(
                    "returnStatus",
                    {
                        id: "returnStatus",
                        header: "Return",
                        cell: (info) => (
                            <Status
                                value={
                                    info.getValue()
                                }
                            />
                        ),
                    }
                ),

                columnHelper.accessor(
                    "conversionStatus",
                    {
                        id: "conversionStatus",
                        header: "Conversion",
                        cell: (info) => (
                            <Status
                                value={
                                    info.getValue()
                                }
                            />
                        ),
                    }
                ),

            ];


        /*
        |--------------------------------------------------------------------------
        | GPS COMPLIANCE
        |--------------------------------------------------------------------------
        */

        case "gps_compliance":

            return [

                columnHelper.accessor(
                    "date",
                    {
                        id: "date",
                        header: "Date",
                        cell: (info) =>
                            formatDate(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "employee.name",
                    {
                        id: "employee",
                        header: "Employee",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "checkInGpsAvailable",
                    {
                        id: "checkInGps",
                        header: "Check-In GPS",
                        cell: (info) => (
                            info.getValue()
                                ? "Yes"
                                : "No"
                        ),
                    }
                ),

                columnHelper.accessor(
                    "checkOutGpsAvailable",
                    {
                        id: "checkOutGps",
                        header: "Check-Out GPS",
                        cell: (info) => (
                            info.getValue()
                                ? "Yes"
                                : "No"
                        ),
                    }
                ),

            ];


        /*
        |--------------------------------------------------------------------------
        | JOURNEY COMPLIANCE
        |--------------------------------------------------------------------------
        */

        case "journey_compliance":

            return [

                columnHelper.accessor(
                    "planDate",
                    {
                        id: "planDate",
                        header: "Date",
                        cell: (info) =>
                            formatDate(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "employee.name",
                    {
                        id: "employee",
                        header: "Employee",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "title",
                    {
                        id: "title",
                        header: "Plan",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "plannedVisits",
                    {
                        id: "plannedVisits",
                        header: "Planned Visits",
                    }
                ),

                columnHelper.accessor(
                    "status",
                    {
                        id: "status",
                        header: "Status",
                        cell: (info) => (
                            <Status
                                value={
                                    info.getValue()
                                }
                            />
                        ),
                    }
                ),

            ];


        /*
        |--------------------------------------------------------------------------
        | SALES PERSON PERFORMANCE
        |--------------------------------------------------------------------------
        */

        case "sales_person_performance":

            return [

                columnHelper.accessor(
                    "employee.name",
                    {
                        id: "employee",
                        header: "Sales Person",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "employee.employeeCode",
                    {
                        id: "employeeCode",
                        header: "Code",
                        cell: (info) =>
                            info.getValue() ||
                            "-",
                    }
                ),

                columnHelper.accessor(
                    "visits",
                    {
                        id: "visits",
                        header: "Visits",
                    }
                ),

                columnHelper.accessor(
                    "approvedVisits",
                    {
                        id: "approvedVisits",
                        header: "Approved Visits",
                    }
                ),

                columnHelper.accessor(
                    "collections",
                    {
                        id: "collections",
                        header: "Collections",
                    }
                ),

                columnHelper.accessor(
                    "collectionAmount",
                    {
                        id: "collectionAmount",
                        header: "Collection Amount",
                        cell: (info) =>
                            formatCurrency(
                                info.getValue()
                            ),
                    }
                ),

                columnHelper.accessor(
                    "appraisals",
                    {
                        id: "appraisals",
                        header: "Appraisals",
                    }
                ),

                columnHelper.accessor(
                    "samplesIssued",
                    {
                        id: "samplesIssued",
                        header: "Samples",
                    }
                ),

                columnHelper.accessor(
                    "sampleQuantity",
                    {
                        id: "sampleQuantity",
                        header: "Sample Qty",
                    }
                ),

            ];


        default:

            return [];

    }
};